import React, { useState } from 'react';
import { Box, InputAdornment } from '@mui/material';
import { useForm, FormProvider } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

import CustomCheckbox from '../../../components/CustomCheckbox/CustomCheckbox';
import { RHFTextField } from '../../../components/RHF';
import { RegistrationSchema } from '../utils/validationSchemas/registrationSchema';
import { useRegistrationMutation } from '../hooks/mutations/authMutations';
import { RegistrationProps } from '../types';
import logoImage from '../../../assets/images/logo.svg';
import userImage from '../../../assets/images/user.svg';
import callIconImage from '../../../assets/images/callIcon.svg';
import emailImage from '../../../assets/images/email.svg';
import { CustomButton } from 'components/Button';

const Registration: React.FC<RegistrationProps> = ({ setSignup }) => {
  const [agree, setAgree] = useState<boolean>(false);

  const registrationMutation = useRegistrationMutation();

  const defaultValues = {
    name: '',
    phone_number: '',
    email_address: '',
  };

  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement> | { target: { checked: boolean } }) => {
    if ('target' in event) {
      setAgree(event.target.checked);
    }
  };

  const methods = useForm({
    resolver: yupResolver(RegistrationSchema),
    defaultValues,
  });

  const onSubmit = (data: any) => {
    const formData = {
      ...data,
      agree: agree
    };

    registrationMutation.mutate(formData, {
      onSuccess: (response: any) => {
        if (response.success) {
          setSignup(false);
        }
      }
    });
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(onSubmit)}>
        <Box className={"auth-form-container"}>
          <img src={logoImage} alt="logo" className="auth-form-logo d-block" />

          <p className="divider" />

          <p className="auth-form-title">Sign Up</p>
          <p className="auth-form-text">
            Sign up to get started with easy lottery management and smarter store tracking.
          </p>

          <Box className={"textfieldCont"}>
            <RHFTextField
              name="name"
              placeholder="Name"
              slotProps={{
                input: {
                  startAdornment: <InputAdornment position="start"><img src={userImage} alt="user" /></InputAdornment>,
                },
              }}
            />
          </Box>

          <Box className={"textfieldCont"}>
            <RHFTextField
              name="phone_number"
              placeholder="Phone Number"
              maxLength={10}
              slotProps={{
                input: {
                  startAdornment: <InputAdornment position="start"><img src={callIconImage} alt="phone" /></InputAdornment>,
                },
              }}
            />
          </Box>

          <Box className={"textfieldCont"}>
            <RHFTextField
              name="email_address"
              placeholder="Email Address"
              slotProps={{
                input: {
                  startAdornment: <InputAdornment position="start"><img src={emailImage} alt="email" /></InputAdornment>,
                },
              }}
            />
          </Box>

          <div className="d-flex align-center">
            <CustomCheckbox checked={agree} onChange={handleCheckboxChange} className="mr-8" />
            <p className="agree-text">
              I agree to the{" "}
              <span className="highlight-blue" onClick={() => window.open('https://lottoreco.com/terms-of-service', '_blank')}>Terms & Conditions{" "}</span>
              and{" "}
              <span className="highlight-blue" onClick={() => window.open('https://lottoreco.com/privacy-policy', '_blank')}>Privacy Policy</span>.
            </p>
          </div>

          <Box>
            <CustomButton
              type="submit"
              loading={registrationMutation.isPending}
              disabled={!agree}
              className="button-base button-compact auth-form-button"
            >
              Sign Up
            </CustomButton>
          </Box>
          <p className="divider" />
          <p className="do-not-have-account">
            I already have an account?{" "}
            <span className="signup-text" onClick={() => setSignup(false)}>Sign In</span>
          </p>
        </Box>
      </form>
    </FormProvider>
  );
};

export default Registration; 